import { bind } from '@react-rxjs/core';
import { createSignal, partitionByKey } from '@react-rxjs/utils';
import dayjs from 'dayjs';
import {
  catchError,
  concat,
  EMPTY,
  filter,
  from,
  ignoreElements,
  map,
  merge,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
  throttleTime,
} from 'rxjs';
import { Trip, TripResponse } from '../pages/History/types';
import { DateRange, UUID } from '../types';
import { request } from './request';
import { TripType } from './tripType';

const intlOpts = Intl?.DateTimeFormat().resolvedOptions() ?? {};

export const [customRange$, setCustomRange] = createSignal<DateRange>();
export const [today$, setToday] = createSignal();
export const [last3Days$, setLast3Days] = createSignal();
export const [currentWeek, setCurrentWeek] = createSignal();

const getToday = (): DateRange => [
  dayjs().startOf('day').toISOString(),
  dayjs().endOf('day').toISOString(),
];

const dateRange$ = merge(
  customRange$,
  today$.pipe(map(getToday)),
  last3Days$.pipe(
    map(
      (): DateRange => [
        dayjs().subtract(2, 'day').startOf('day').toISOString(),
        dayjs().endOf('day').toISOString(),
      ]
    )
  ),
  currentWeek.pipe(
    map(
      (): DateRange => [
        dayjs().day(1).startOf('day').toISOString(),
        dayjs().endOf('day').toISOString(),
      ]
    )
  )
);

const validateRange = ([from, to]: DateRange): string | null => {
  if (!from || !to) {
    return 'Date.Range.Error.Required';
  }

  if (from > to) {
    return 'Date.Range.Error.Validity';
  }

  if (Math.abs(dayjs(from).diff(to, 'days')) > 31) {
    return 'Date.Range.Error.Exceeded';
  }

  return null;
};

export const [useDateRangeValidation, dateRangeValidation$] = bind(
  dateRange$.pipe(
    map((dateRange) => ({ dateRange, error: validateRange(dateRange) }))
  ),
  { dateRange: getToday(), error: null }
);

const history$ = dateRangeValidation$.pipe(
  filter(({ error }) => !error),
  throttleTime(300, undefined, { leading: false, trailing: true }),
  switchMap(({ dateRange: [from, to] }) =>
    request<TripResponse>(
      `/gateway/history-service/v20181109/trips?fromDatetime=${from}&toDatetime=${to}`,
      {
        headers: {
          'Ruptela-Timezone': intlOpts.timeZone,
          'Ruptela-Language': intlOpts.locale,
        },
      }
    ).pipe(catchError(() => EMPTY))
  ),
  shareReplay(1)
);

export const [useHistory] = bind(history$, null);

export type HistoryButtonState = 'today' | 'last3' | 'currentWeek' | 'custom';

export const [useHistoryActiveButton] = bind(
  merge(
    customRange$.pipe(map((): HistoryButtonState => 'custom')),
    today$.pipe(map((): HistoryButtonState => 'today')),
    last3Days$.pipe(map((): HistoryButtonState => 'last3')),
    currentWeek.pipe(map((): HistoryButtonState => 'currentWeek'))
  ).pipe(shareReplay(1)),
  'today'
);

export const [useDateRange] = bind(dateRange$, getToday());

export const [getTripById] = partitionByKey(
  history$.pipe(switchMap((history) => from(history.trips))),
  (value) => value.flatTrip.id
);

export const [openEditNoteDialog$, openEditNote] = createSignal<UUID>();
export const [closeEditNoteDialog$, closeEditNote] = createSignal();
export const [saveTripNoteSignal$, saveTripNote] = createSignal<string>();

export const saveTripNote$ = openEditNoteDialog$.pipe(
  switchMap((tripId) =>
    saveTripNoteSignal$.pipe(
      switchMap((note) =>
        request(
          `/gateway/object-trip-api-service/v20171106/objects/null/flat-trips/${tripId}/last-note`,
          {
            method: 'PUT',
            body: JSON.stringify({ value: note }),
            headers: { 'Content-Type': 'application/json' },
          }
        ).pipe(
          map(() => ({ note, tripId })),
          catchError(() => EMPTY)
        )
      )
    )
  )
);

const [getSavedNoteById, savedNoteIds$] = partitionByKey(
  saveTripNote$,
  ({ tripId }) => tripId
);

export const [openEditTripTypeDialog$, openEditTripType] = createSignal<UUID>();
export const [closeEditTripTypeDialog$, closeEditTripType] = createSignal();
export const [saveTripignal$, saveTripType] = createSignal<TripType>();

export const saveTripType$ = openEditTripTypeDialog$.pipe(
  switchMap((tripId) =>
    saveTripignal$.pipe(
      switchMap((tripType) =>
        request(
          `/gateway/object-trip-api-service/v20171106/objects/null/flat-trips/${tripId}/last-trip-type-override`,
          {
            method: 'PUT',
            body: JSON.stringify({ tripType, overridden: true }),
            headers: { 'Content-Type': 'application/json' },
          }
        ).pipe(
          map(() => ({ tripType, tripId })),
          catchError(() => EMPTY)
        )
      )
    )
  )
);

const [getSavedTripTypeById, savedTripTypeIds$] = partitionByKey(
  saveTripType$,
  ({ tripId }) => tripId
);

export const [useTripData, tripData] = bind(
  (id: UUID) =>
    getTripById(id).pipe(
      switchMap((trip) =>
        merge(
          getSavedTripTypeById(id).pipe(
            map(
              ({ tripType }): Trip => ({
                ...trip,
                flatTrip: {
                  ...trip.flatTrip,
                  tripTypeOverride: { overridden: true, tripType },
                },
              })
            )
          ),
          getSavedNoteById(id).pipe(
            map(
              ({ note }): Trip => ({
                ...trip,
                flatTrip: { ...trip.flatTrip, note },
              })
            )
          )
        ).pipe(startWith(trip))
      )
    ),
  null
);

export const [useEditNoteAlertState] = bind(
  merge(
    closeEditNoteDialog$.pipe(map(() => null)),
    /** 2nd emmision means new value is saved and we can close the dialog */
    openEditNoteDialog$.pipe(
      switchMap((id) => concat(tripData(id).pipe(take(2)), of(null)))
    ),
    /** just subscribing to savedNoteIds$ for getSavedNoteById to work */
    savedNoteIds$.pipe(ignoreElements())
  ),
  null
);

export const [useEditTripTypeAlertState] = bind(
  merge(
    closeEditTripTypeDialog$.pipe(map(() => null)),
    /** 2nd emmision means new value is saved and we can close the dialog */
    openEditTripTypeDialog$.pipe(
      switchMap((id) => concat(tripData(id).pipe(take(2)), of(null)))
    ),
    /** just subscribing to savedTripTypeIds$ for getSavedTripTypeById to work */
    savedTripTypeIds$.pipe(ignoreElements())
  ),
  null
);

export const [toggleTripMap$, toggleTripMap] = createSignal<UUID | null>();
export const [useShowTripMap] = bind(toggleTripMap$, null);

export const [useTripImage] = bind(
  ({
    width,
    height,
    tripId,
  }: Partial<{ width: number; height: number; tripId: UUID }>) => {
    if (width == null || height == null || !tripId) {
      return EMPTY;
    }
    return getTripById(tripId).pipe(
      filter((trip) => !!trip.routeWithIgnitionOff),
      switchMap((trip) =>
        request<string>(
          encodeURI(
            `/gateway/map-signing-service/v20220808/sign?size=${width}x${height}&path=weight:6|color:0x0F3FFBff|enc:${
              trip.routeWithIgnitionOn.polyline
            }&markers=color:0x2bc3fbff|label:A|${
              trip.routeWithIgnitionOn.firstCoordinate.latitude
            },${
              trip.routeWithIgnitionOn.firstCoordinate.longitude
            }&markers=color:0x2bc3fbff|label:B|${
              trip.routeWithIgnitionOff!.firstCoordinate.latitude
            },${
              trip.routeWithIgnitionOff!.firstCoordinate.longitude
            }&style=feature:road|color:0xffffff&style=all|element:labels.text.fill|color:0x939FB4&scale=${
              devicePixelRatio > 1 ? 2 : 1
            }`
          )
        ).pipe(catchError(() => EMPTY))
      ),
      map((src) => ({ src, height, width }))
    );
  },
  null
);
